import {
  MAXIMUM_CHARACTERS_SEARCH,
  MINIMUM_CHARACTERS_SEARCH,
  ORPHAN_REVIEW_COMPANY_ID,
} from '@seek/libs-shared';
import { MultiselectAutosuggest } from '@seek/multiselect-autosuggest';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Column,
  Columns,
  IconSearch,
  Stack,
  Text,
  TextField,
  TextLink,
  TextLinkButton,
  useResponsiveValue,
} from 'braid-design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import type { SearchCompanyProfileResult } from '../../../../shared/services/companyProfileService/types';

import translations from './.vocab';

const UNABLE_TO_FIND_COMPANY = 'unableToFind';

export interface CompanyNameValueV2 {
  mode: 'search' | 'add';
  companyId?: string;
  companyName?: string;
  organisationId?: string;
  published?: boolean;
}

export type CompanyNameQuestionV2Props = {
  companyName: string;
  mode: 'search' | 'add';
  onChange: (value: CompanyNameValueV2) => void;
  searchCompanyProfiles: (query: string) => Promise<SearchCompanyProfileResult>;
  errorMessage?: string;
};

type CompanyNameAutoSuggestProps = Omit<CompanyNameQuestionV2Props, 'mode'>;

type Suggestion =
  | {
      id: string;
      name: string;
      organisationId: string;
      type: 'company';
      icon?: string;
      published?: boolean;
    }
  | {
      id: string;
      name: string;
      type: typeof UNABLE_TO_FIND_COMPANY;
    };

const SuggestedCompany = ({
  icon,
  name,
  textSize,
}: {
  name: string;
  textSize: 'small' | undefined;
  icon?: string;
}) => {
  return (
    <Columns space="xsmall" alignY="center" align="center">
      <Column width="content">
        {icon ? (
          <Box
            component="img"
            src={icon}
            alt={`Company Logo for ${name}`}
            style={{
              width: '24px',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Box
            style={{
              width: '24px',
              height: '24px',
            }}
          />
        )}
      </Column>
      <Column>
        <Text size={textSize}>{name}</Text>
      </Column>
    </Columns>
  );
};

const CompanyNameAutoSuggest = ({
  onChange,
  companyName,
  errorMessage,
  searchCompanyProfiles,
}: CompanyNameAutoSuggestProps) => {
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  const { t } = useTranslations(translations);
  const [debouncedSearchTerm] = useDebounce(companyName, 400);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion>();
  const searchInputContainer = useRef<HTMLDivElement>(null);

  const onFocus = useCallback(() => {
    if (isMobile) {
      searchInputContainer.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isMobile]);

  const onSetSearchText = useCallback(
    (newText: string) => {
      setSelectedSuggestion(undefined);

      if (
        newText.length < MINIMUM_CHARACTERS_SEARCH ||
        newText.length > MAXIMUM_CHARACTERS_SEARCH
      ) {
        setSuggestions([]);
      }

      onChange({
        companyName: newText,
        mode: 'search',
      });
    },
    [onChange],
  );

  const onClickSuggestionItem = useCallback(
    (item: Suggestion) => {
      if (item.type === 'company') {
        setSelectedSuggestion(item);
        onChange({
          companyName: item.name,
          companyId: item.id,
          organisationId: item.organisationId,
          published: item.published,
          mode: 'search',
        });
      } else {
        setSelectedSuggestion(undefined);
        onChange({
          companyName,
          companyId: ORPHAN_REVIEW_COMPANY_ID,
          mode: 'add',
        });
      }

      setSuggestions([]);
    },
    [onChange, companyName],
  );

  useEffect(() => {
    const fetchCompanies = async (text: string) => {
      const companiesList = await searchCompanyProfiles(text);

      const companySuggestions: Suggestion[] = companiesList.map((company) => ({
        id: company.companyId,
        name: company.data.name,
        organisationId: company.organisationId,
        published: company.data.published,
        icon: company.data.branding?.logoImageUrl,
        type: 'company',
      }));

      setSuggestions(
        companySuggestions.concat([
          {
            id: UNABLE_TO_FIND_COMPANY,
            name: UNABLE_TO_FIND_COMPANY,
            type: UNABLE_TO_FIND_COMPANY,
          },
        ]),
      );
    };

    const debouncedSearchTermText = debouncedSearchTerm?.trim();
    if (
      debouncedSearchTermText &&
      selectedSuggestion?.name !== debouncedSearchTermText &&
      debouncedSearchTermText.length >= MINIMUM_CHARACTERS_SEARCH &&
      debouncedSearchTermText.length <= MAXIMUM_CHARACTERS_SEARCH
    ) {
      void fetchCompanies(debouncedSearchTermText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  return (
    <Box ref={searchInputContainer}>
      <MultiselectAutosuggest
        id="companyName"
        label={`${t('Company name')}`}
        aria-label={`${t('Company name')}`}
        description={t('Well add this company to your SEEK Profile')}
        searchText={companyName}
        setSearchText={onSetSearchText}
        placeholder={t('Search company placeholder')}
        onFocus={onFocus}
        onClickSuggestionItem={onClickSuggestionItem}
        /**
         * Intentionally leave both `onItemsSelected` and `selectedItems` empty
         * as this component is not used for selecting items, but rather for searching
         * and displaying suggestions based on the search input.
         */
        onItemsSelected={() => {}}
        selectedItems={[]}
        renderSelectedItems={() => (
          <Text size="small">
            <IconSearch />
          </Text>
        )}
        tone={errorMessage ? 'critical' : undefined}
        message={errorMessage}
        clearLabel={t('Clear')}
        suggestions={suggestions}
        renderSuggestions={({ getItemProps, highlightedIndex }) => {
          return (
            <>
              {suggestions.map((suggestion, itemIndex) => {
                const isHighlighted = highlightedIndex === itemIndex;

                return (
                  <Box
                    key={`suggestion-item-${suggestion.id}-${suggestion.type === 'company' ? suggestion.organisationId : 'not-found'}`}
                    component="li"
                    background={
                      isHighlighted ? 'formAccentSoftHover' : undefined
                    }
                    cursor="pointer"
                    padding="small"
                    {...getItemProps({
                      item: suggestion,
                      name: suggestion.name,
                      index: itemIndex,
                    })}
                  >
                    {suggestion.type === 'company' ? (
                      <SuggestedCompany
                        icon={suggestion.icon}
                        name={suggestion.name}
                        textSize={isMobile ? 'small' : undefined}
                      />
                    ) : (
                      <Text size={isMobile ? 'small' : undefined}>
                        {t('Add a company suggested option', {
                          TextLink: (text) => (
                            <TextLink href="#">{text}</TextLink>
                          ),
                        })}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </>
          );
        }}
      />
    </Box>
  );
};

export const CompanyNameQuestionV2 = ({
  errorMessage,
  onChange,
  searchCompanyProfiles,
  mode,
  companyName,
}: CompanyNameQuestionV2Props) => {
  const { t } = useTranslations(translations);

  if (mode === 'search') {
    return (
      <CompanyNameAutoSuggest
        companyName={companyName}
        onChange={onChange}
        searchCompanyProfiles={searchCompanyProfiles}
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <Stack space="medium">
      <TextField
        id="companyName"
        label={t('Add company label')}
        aria-label={t('Add company label')}
        description={t('Add company description')}
        value={companyName}
        onChange={(e) => {
          onChange({
            companyName: e.currentTarget.value,
            companyId: ORPHAN_REVIEW_COMPANY_ID,
            mode: 'add',
          });
        }}
        tone={errorMessage ? 'critical' : undefined}
        message={errorMessage}
      />
      <Text size="small">
        <TextLinkButton
          onClick={() => {
            onChange({
              companyName,
              mode: 'search',
            });
          }}
        >
          {t('Use company search')}
        </TextLinkButton>
      </Text>
      <Alert tone="info">
        <Text>{t('Orphaned company review notification')}</Text>
      </Alert>
    </Stack>
  );
};
